import React, { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Template } from '../types/template';
import { Category } from '../types/category';
import Pagination from './Pagination';

interface TemplateListProps {
  templates: Template[];
  loading: boolean;
  categories: Category[];
  onEdit: (template: Template) => void;
  onDelete: (template: Template) => void;
}

const ITEMS_PER_PAGE = 10;

const TemplateList: React.FC<TemplateListProps> = ({ templates, loading, categories, onEdit, onDelete }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const getCategoryName = (catId: string | number) => {
    const category = categories.find(cat => String(cat.id) === String(catId));
    return category?.category || catId;
  };

  if (loading && templates.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-gray-500">Loading templates...</p>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-gray-500">No templates found. Create your first template!</p>
      </div>
    );
  }

  const totalPages = Math.ceil(templates.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTemplates = templates.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to last valid page if items are deleted
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(totalPages);
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800">Template List</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                TYPE
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CATEGORY
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                SUBCATEGORY
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                AI MODEL
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                PROMPT
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reference images
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedTemplates.map((template) => (
              <tr key={template.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {template.template_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {template.type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {getCategoryName(template.cat_id)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {template.subcategory || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {template.ai_model || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                  {template.prompt}
                </td>
                <td className="px-6 py-4">
                  {template.output && Array.isArray(template.output) && template.output.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {template.output.map((url, index) => (
                        <div key={index} className="relative">
                          <img
                            src={url}
                            alt={`Output ${index + 1}`}
                            className="h-16 w-16 object-cover rounded border border-gray-300"
                          />
                          {template.output.length > 1 && (
                            <span className="absolute top-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded-bl">
                              {index + 1}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">No images</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-3">
                    <button
                      onClick={() => onEdit(template)}
                      className="text-green-600 hover:text-green-700 transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDelete(template)}
                      className="text-red-600 hover:text-red-700 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={templates.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default TemplateList;
